import { useEffect, useRef } from 'react';
import { ZONES } from './zones';

interface MobileNavProps {
  isActive: (path: string) => boolean;
  onNavigate: (path: string) => void;
  onOpenCommand: () => void;
  open: boolean;
}

export default function MobileNav({ isActive, onNavigate, onOpenCommand, open }: MobileNavProps) {
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const nav = navRef.current;
    if (!nav || !open) return;
    const containWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.deltaY === 0) return;
      const atTop = event.deltaY < 0 && nav.scrollTop <= 0;
      const atBottom = event.deltaY > 0 && nav.scrollTop >= nav.scrollHeight - nav.clientHeight - 1;
      if (atTop || atBottom) event.preventDefault();
    };
    // Firefox can chain wheel scrolling from a sticky container despite CSS containment.
    nav.addEventListener('wheel', containWheel, { passive: false });
    return () => nav.removeEventListener('wheel', containWheel);
  }, [open]);
  if (!open) return null;

  const activeClass = 'bg-accent-50 text-accent-700';
  const inactiveClass = 'text-surface-500 hover:bg-surface-100 hover:text-surface-900';

  return (
    <nav
      ref={navRef}
      id="mobile-navigation"
      aria-label="Мобильная навигация"
      className="min-h-0 overflow-y-auto overscroll-y-contain border-t border-surface-200 bg-white px-4 pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-3 md:hidden"
    >
      <button
        type="button"
        onClick={onOpenCommand}
        className="mb-4 flex min-h-11 w-full items-center gap-2 rounded-md border border-surface-200 px-3 text-sm text-surface-500 transition-colors hover:bg-surface-100 hover:text-surface-900"
      >
        <i className="ri-search-line" aria-hidden="true" />
        Поиск
      </button>

      <div className="grid gap-4 sm:grid-cols-2">
        {ZONES.map((zone) => (
          <div key={zone.id}>
            <p className="mb-1.5 px-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-surface-400">
              {zone.label}
            </p>
            <div className="space-y-1">
              {zone.items
                .flatMap((item) => item.children ?? [item])
                .map((item) => {
                  const active = isActive(item.path);
                  return (
                    <button
                      type="button"
                      aria-current={active ? 'page' : undefined}
                      key={`${zone.id}-${item.path}`}
                      onClick={() => onNavigate(item.path)}
                      className={`flex min-h-11 w-full items-center gap-2.5 rounded-md px-3 text-left text-sm font-medium transition-colors ${
                        active ? activeClass : inactiveClass
                      }`}
                    >
                      <i className={`${item.icon} text-base`} aria-hidden="true" />
                      {item.label}
                    </button>
                  );
                })}
            </div>
          </div>
        ))}
      </div>
    </nav>
  );
}
